import { Box, Grid, TextField, Typography } from "@mui/material";
import { Accordion } from "../../../main/components/accordion/Accordion";
import { useState, useContext } from "react";
import { AlarmDetails } from "../types";
import { alarmsApi } from "../../../services/api";
import { FiEdit2 } from "react-icons/fi";
import { MainContext } from "../../../main/context/MainContextProvider";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import BaseButton from "../../../main/components/BaseButton";
import ConfirmationDialog from "./ConfirmationDialog";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";
import * as yup from "yup";
import { CardAccordionsSolutions } from "./AlarmCardAccordionsSolutions";
import CardDownload from "../../faq/components/CardDownload";
import { EditIconButton } from "../../../main/components/EditIconButton";
import AddFileModal from "./AddFileModal";

const possibleCausesSchema = yup.object().shape({
  possibleCauses: yup.string().trim().nullable(),
});

const possibleSolutionsSchema = yup.object().shape({
  possibleSolutions: yup.string().trim().nullable(),
});

export type EditPossibleCauses = yup.InferType<typeof possibleCausesSchema>;
export type EditPossibleSolutions = yup.InferType<
  typeof possibleSolutionsSchema
>;

export function CardAccordions(props: {
  alarmDetails: AlarmDetails | null;
  onMount: () => void;
}) {
  const { alarmDetails, onMount } = props;
  const path = window.location.pathname;
  const alarmCode = path.split("/")[3];
  const [showPossibleCausesDetails, setShowPossibleCausesDetails] =
    useState<boolean>(false);
  const togglePossibleCausesDetails = () => {
    setShowPossibleCausesDetails(!showPossibleCausesDetails);
  };
  const [editCauses, setEditCauses] = useState<boolean>(false);
  const [possibleCauses, setPossibleCauses] = useState(
    alarmDetails?.causes || ""
  );
  const [, setCurrentCauses] = useState("");
  const { theme } = useContext(MainContext);
  const primary = theme.muiTheme.palette.primary.main;
  const [, setSaveConfirmationOpen] = useState<boolean>(false);
  const [isCancelConfirmationOpen, setCancelConfirmationOpen] =
    useState<boolean>(false);
  const [addFileModalOpen, setAddFileModalOpen] = useState<boolean>(false);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openFileSuccessModal, setOpenFileSuccessModal] =
    useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const [loadingSaveFile, setLoadingSaveFile] = useState<boolean>(false);
  const [openFileErrorModal, setOpenFileErrorModal] = useState<boolean>(false);
  const [showAddItemConfirmationModal, setShowAddItemConfirmationModal] =
    useState<boolean>(false);
  const [originalCauses, setOriginalCauses] = useState("");

  const handleEditCauses = () => {
    // Armazena o valor original ao iniciar a edição
    setOriginalCauses(possibleCauses);
    setEditCauses(true);
  };

  const handleSaveButton = () => {
    setShowAddItemConfirmationModal(true);
  };

  const handleSaveCauses = async () => {
    try {
      const possibleCausesEdit = await alarmsApi.patchPossibleCauses(
        alarmCode,
        possibleCauses
      );
      console.log(`Salvando mensagem: "${possibleCauses}"`, possibleCausesEdit);
      setEditCauses(false);
      localStorage.setCurrentCauses(`${possibleCauses}`, possibleCausesEdit);
      setCurrentCauses(possibleCauses);
    } catch (error) {
      // Lida com erros, se necessário
    }
  };

  const handleCancelCauses = () => {
    setCancelConfirmationOpen(true);
  };

  const handleCancelCausesModal = () => {
    if (editCauses) {
      // Reverte as alterações ao estado original
      setPossibleCauses(originalCauses);
      setEditCauses(false);
      setCancelConfirmationOpen(false);
    } else {
      setSaveConfirmationOpen(false);
    }
  };

  const handleClickInspectionProcedure = () => {
    if (alarmDetails?.actions?.hasInspectionProcedure) {
      const downloadButton = document.createElement("a");
      downloadButton.href = alarmDetails.actions.inspectionProcedure ?? "";
      downloadButton.download = alarmDetails.actions.inspectionProcedure ?? "";
      downloadButton.click();
    }
  };

  const [file, setFile] = useState<File | null>(null);

  const handleInspectionProcedureModal = async () => {
    setLoadingSaveFile(true);
    if (file) {
      try {
        await alarmsApi.patchInspectionProcedures(alarmCode, file);
        setAddFileModalOpen(false);
        setOpenFileSuccessModal(true);
        setLoadingSaveFile(false);
      } catch (error) {
        setOpenFileErrorModal(true);
        setLoadingSaveFile(false);
      }
    } else {
      setOpenErrorModal(true);
      setLoadingSaveFile(false);
    }
  };

  return (
    <>
      <Accordion.Root>
        <Accordion.Header
          toggleDetails={togglePossibleCausesDetails}
          showDetails={showPossibleCausesDetails}
          title="Possíveis causas"
          typographySx={{ fontSize: { xs: "13px", sm: "16px" } }}
        />
        <Accordion.Content showDetails={showPossibleCausesDetails}>
          <Grid container alignItems="center">
            {!editCauses && (
              <FiEdit2
                onClick={() => handleEditCauses()}
                style={{
                  marginRight: "10px",
                }}
              />
            )}
            {editCauses ? (
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <TextField
                    label="Possíveis causas"
                    value={possibleCauses}
                    onChange={(e) => setPossibleCauses(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item container justifyContent="flex-end" spacing={2}>
                  <Grid item>
                    <DangerBaseButton
                      onClick={handleCancelCauses}
                      title="Cancelar"
                      sx={{
                        height: "34px",
                        width: "95px",
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <BaseButton
                      onClick={handleSaveButton}
                      sx={{
                        backGroundColor: primary,
                        borderColor: primary,
                        height: "34px",
                        width: "95px",
                        "& .MuiTypography-root": {
                          color: primary,
                        },
                        "&:hover": {
                          borderColor: primary,
                          backgroundColor: primary,
                          color: primary,
                          "& .MuiTypography-root": {
                            color: "#FFFFFF",
                          },
                        },
                      }}
                      title="Confirmar"
                      type="submit"
                    />
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <>
                <Grid
                  style={{
                    fontWeight: "400",
                    fontSize: " 0.9rem",
                  }}
                >
                  {possibleCauses ?? "Não informado"}
                  {/* {alarmDetails?.causes ?? "Não informado"} */}
                </Grid>
              </>
            )}
          </Grid>
        </Accordion.Content>
      </Accordion.Root>
      {/* Possíveis Soluções */}
      <CardAccordionsSolutions alarmDetails={alarmDetails} onMount={onMount} />
      {/* Procedimentos de Inspeção */}
      <Box sx={{ width: "100%", display: "flex", marginTop: "16px" }}>
        <Typography
          sx={{
            fontSize: { xs: "13px", sm: "16px" },
            color: "#FFF",
            marginRight: "8px",
          }}
        >
          Procedimentos de inspeção
        </Typography>
        <EditIconButton
          onClick={() => {
            setAddFileModalOpen(true);
          }}
          tooltipText={
            alarmDetails?.actions?.hasInspectionProcedure
              ? "Editar documento de procedimnto de inpeção"
              : "Adicionar documento de inspeção"
          }
        />
      </Box>
      <Grid sx={{ paddingY: "15px" }}>
        <CardDownload
          sx={{
            cursor: alarmDetails?.actions?.hasInspectionProcedure
              ? "pointer"
              : "default",
          }}
          label={
            alarmDetails?.actions?.hasInspectionProcedure
              ? "Clique aqui para baixar o documento de procedimentos de inspeção."
              : "Nenhum documento de procedimento de inspeção encontrado."
          }
          onClick={handleClickInspectionProcedure}
          tooltipText={
            alarmDetails?.actions?.hasInspectionProcedure
              ? ""
              : "Nenhum arquivo encontrado"
          }
        />
      </Grid>

      <ConfirmationDialog
        open={isCancelConfirmationOpen}
        title="Descartar alteração"
        message="Deseja descartar e cancelar as alterações?"
        onConfirm={handleCancelCausesModal}
        onCancel={() => setCancelConfirmationOpen(false)}
        onClose={() => setCancelConfirmationOpen(false)}
      />
      <ActionAlert
        message="O documento foi atualizado com sucesso!"
        severity="success"
        open={openFileSuccessModal}
        setOpen={() => setOpenFileSuccessModal(false)}
        onClose={() => onMount()}
      />

      <AddFileModal
        file={file}
        setFile={setFile}
        open={addFileModalOpen}
        loadingSaveFile={loadingSaveFile}
        title={
          alarmDetails?.actions?.hasInspectionProcedure
            ? "Editar arquivo"
            : "Adicionar arquivo"
        }
        label="Arraste um documento de procedimento de inspeção aqui, ou clique para selecionar!"
        onConfirm={handleInspectionProcedureModal}
        onCancel={() => {
          setAddFileModalOpen(false);
          setFile(null);
        }}
        onClose={() => {
          setAddFileModalOpen(false);
          setFile(null);
        }}
      />
      <ConfirmationModal
        open={showAddItemConfirmationModal}
        setOpen={setShowAddItemConfirmationModal}
        title="Editar detalhes"
        contentText="Deseja confirmar a edição dos detalhes dos alarmes?"
        onConfirm={handleSaveCauses}
        setOpenErrorModal={setOpenErrorModal}
        setOpenSuccessModal={setOpenSuccessModal}
      />
      <ActionAlert
        message="Os detalhes do alarme foram atualizados com sucesso!"
        severity="success"
        open={openSuccessModal}
        setOpen={() => setOpenSuccessModal(false)}
        onClose={() => onMount()}
      />

      <ActionAlert
        message="Ocorreu um erro ao editar os detalhes do alarme! Tente novamente mais tarde."
        severity="error"
        open={openErrorModal}
        setOpen={setOpenErrorModal}
      />

      <ActionAlert
        message="Ocorreu um erro ao editar o documento! Tente novamente mais tarde."
        severity="error"
        open={openFileErrorModal}
        setOpen={setOpenFileErrorModal}
      />
    </>
  );
}
